import { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Info, TrendingUp, Trophy } from "lucide-react";
import { API } from "../config";
import { PersonaHeader } from "../components/persona/PersonaSections";

const T = {
  sk: {
    title: "Rebríček zlepšení webov", sub: "O koľko bodov sa zlepšili weby, ktoré prešli opakovanou kontrolou TopRankPulse.",
    note: "Iba skutočné merania: skóre z prvého a posledného auditu TopRankPulse s dátumom. Názvy zobrazujeme len so súhlasom majiteľa, ostatné weby sú anonymné. Metodika auditu sa medzičasom mohla spresniť.",
    empty: "Zatiaľ tu nie sú žiadne opakované audity so zlepšením.",
    before: "prvý audit", after: "posledný audit", points: "bodov", cta: "Skontrolovať môj web zadarmo", ctaHref: "/gratulujeme/sk/web", other: "Česká verzia", otherHref: "/cz/zebricek-zlepseni", lang: "sk",
  },
  cs: {
    title: "Žebříček zlepšení webů", sub: "O kolik bodů se zlepšily weby, které prošly opakovanou kontrolou TopRankPulse.",
    note: "Pouze skutečná měření: skóre z prvního a posledního auditu TopRankPulse s datem. Názvy zobrazujeme jen se souhlasem majitele, ostatní weby jsou anonymní. Metodika auditu se mezitím mohla zpřesnit.",
    empty: "Zatím tu nejsou žádné opakované audity se zlepšením.",
    before: "první audit", after: "poslední audit", points: "bodů", cta: "Zkontrolovat můj web zdarma", ctaHref: "/gratulujeme/cz/web", other: "Slovenská verze", otherHref: "/rebricek-zlepseni", lang: "cs",
  },
};

const fmt = (d, lang) => (d ? new Date(d).toLocaleDateString(lang === "cs" ? "cs-CZ" : "sk-SK") : "");

const Bar = ({ v, color }) => (
  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className={`h-full rounded-full ${color} transition-[width] duration-1000`} style={{ width: `${v}%` }} /></div>
);

const Row = ({ r, t, rank }) => (
  <li className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-white/25 transition-colors" data-testid={`leaderboard-row-${rank}`}>
    <div className="flex flex-wrap items-center gap-3 mb-3">
      <span className="w-8 h-8 rounded-full bg-[#2563eb]/20 text-[#93c5fd] font-black flex items-center justify-center">{rank}</span>
      <div className="flex-1 min-w-[55%] sm:min-w-0">
        {r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="font-black text-white break-all hover:text-[#93c5fd] underline-offset-4 hover:underline" data-testid={`leaderboard-link-${rank}`}>{r.name} ↗</a>
          : <p className="font-black text-white break-all">{r.name}</p>}
        {r.category && <p className="text-xs text-white/50">{r.category}</p>}
      </div>
      <span className="inline-flex items-center gap-1 text-emerald-300 font-black text-lg"><TrendingUp className="w-4 h-4" />+{r.delta} {t.points}</span>
    </div>
    <div className="grid grid-cols-[minmax(92px,auto)_1fr_36px] items-center gap-x-3 gap-y-1.5 text-xs text-white/60">
      <span>{t.before} <span className="text-white/40">{fmt(r.before_date, t.lang)}</span></span><Bar v={r.before} color="bg-red-400/70" /><span className="text-right font-mono">{r.before}</span>
      <span>{t.after} <span className="text-white/40">{fmt(r.after_date, t.lang)}</span></span><Bar v={r.after} color="bg-emerald-400" /><span className="text-right font-mono text-white">{r.after}</span>
    </div>
  </li>
);

export default function ImprovementsLeaderboardPage({ lang = "sk" }) {
  const t = T[lang];
  const [data, setData] = useState(null);
  useEffect(() => { document.documentElement.lang = lang; axios.get(`${API}/improvements/leaderboard`, { params: { lang } }).then((r) => setData(r.data)).catch(() => {}); }, [lang]);
  return (
    <div className="min-h-screen bg-[#0a1024] text-white" data-testid={`leaderboard-page-${lang}`}>
      <Helmet htmlAttributes={{ lang }}><title>{`${t.title} | TopRankPulse`}</title><meta name="description" content={t.sub} /></Helmet>
      <PersonaHeader ctaLabel={lang === "cs" ? "Balíčky a ceny" : "Balíky a ceny"} />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-300 mb-4"><Trophy className="w-4 h-4" />TopRankPulse</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tight mb-4" data-testid="leaderboard-title">{t.title}</h1>
        <p className="text-base md:text-lg text-white/70 mb-8 max-w-2xl">{t.sub}</p>
        <div className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-5 text-sm text-white/70" data-testid="leaderboard-note">
          <Info className="w-5 h-5 shrink-0 text-[#93c5fd]" />{t.note}
        </div>
        {data && data.rows.length === 0 && <p className="text-white/60" data-testid="leaderboard-empty">{t.empty}</p>}
        <ol className="space-y-3" data-testid="leaderboard-rows">{(data?.rows || []).map((r, i) => <Row key={r.name + i} r={r} t={t} rank={i + 1} />)}</ol>
        <div className="mt-12 flex flex-wrap items-center gap-5">
          <Link to={t.ctaHref} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#2563eb] font-bold hover:bg-[#1d4ed8] transition-colors" data-testid="leaderboard-cta">{t.cta}<ArrowRight className="w-4 h-4" /></Link>
          <Link to={t.otherHref} className="text-sm text-white/60 underline hover:text-white" data-testid="leaderboard-lang-switch">{t.other}</Link>
        </div>
      </main>
    </div>
  );
}
